import { useState } from "react";
import { Link } from "react-router-dom";
import Badge from "components/base/Badge";
import { DealCardProps } from "interfaces/deal";
import { Card } from "react-bootstrap";
import { currencyFormat, dateFormatter } from "helpers/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faDollarSign, faUser, faEllipsisV } from "@fortawesome/free-solid-svg-icons";

const DealCardCompact = ({ deal }: DealCardProps) => {
  const [showMenu, setShowMenu] = useState(false);

  // Calculate days until closing
  const getDaysUntilClosing = () => {
    if (!deal.deal_closing_date) return null;
    const closingDate = new Date(deal.deal_closing_date);
    const today = new Date();
    const days = Math.ceil((closingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const daysUntilClosing = getDaysUntilClosing();
  const isClosingSoon = daysUntilClosing !== null && daysUntilClosing >= 0 && daysUntilClosing <= 7;
  const isOverdue = daysUntilClosing !== null && daysUntilClosing < 0;

  return (
    <Card 
      className="deal-card-compact mb-2"
      style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#00d4ff';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 212, 255, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e5e7eb';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Card.Body style={{ padding: '10px 12px' }}>
        {/* Header Row - Deal Name and Value */}
        <div className="d-flex align-items-center justify-content-between mb-1">
          <Link
            to={`/deals/${deal?.id}?type=activity`}
            className="deal-name"
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#1f2937',
              textDecoration: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '60%',
              display: 'block'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#00d4ff'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#1f2937'}
          >
            {deal?.deal_name || 'Untitled Deal'}
          </Link>
          
          <span 
            style={{
              fontSize: '13px',
              fontWeight: 700,
              color: '#10b981'
            }}
          >
            {currencyFormat(deal.deal_price || 0, { minimumFractionDigits: 0 })}
          </span>
        </div>

        {/* Info Row - Contact and Date */}
        <div className="d-flex align-items-center justify-content-between" style={{ fontSize: '11px', color: '#6b7280' }}>
          <div className="d-flex align-items-center gap-3">
            {/* Customer */}
            <span className="d-flex align-items-center gap-1">
              <FontAwesomeIcon icon={faUser} style={{ fontSize: '9px' }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}>
                {deal?.customers?.[0]?.customer_name || 'No contact'}
              </span>
            </span>

            {/* MLS ID if available */}
            {deal.deal_mls_id && (
              <span style={{ color: '#9ca3af' }}>
                MLS: {deal.deal_mls_id}
              </span>
            )}
          </div>

          {/* Closing Date with urgency indicator */}
          <div className="d-flex align-items-center gap-2">
            {isClosingSoon && (
              <Badge 
                bg="warning"
                style={{
                  fontSize: '9px',
                  padding: '2px 6px',
                  borderRadius: '4px'
                }}
              >
                {daysUntilClosing}d
              </Badge>
            )}
            {isOverdue && (
              <Badge 
                bg="danger"
                style={{
                  fontSize: '9px',
                  padding: '2px 6px',
                  borderRadius: '4px'
                }}
              >
                Overdue
              </Badge>
            )}
            <span className="d-flex align-items-center gap-1">
              <FontAwesomeIcon icon={faCalendar} style={{ fontSize: '9px' }} />
              {deal.deal_closing_date 
                ? new Date(deal.deal_closing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : 'No date'}
            </span>
          </div>
        </div>

        {/* Hover Menu Button */}
        <div
          className="position-absolute"
          style={{
            top: '8px',
            right: '8px',
            opacity: 0,
            transition: 'opacity 0.2s'
          }}
          onMouseEnter={() => setShowMenu(true)}
          onMouseLeave={() => setShowMenu(false)}
        >
          <FontAwesomeIcon 
            icon={faEllipsisV} 
            style={{ 
              fontSize: '12px',
              color: '#9ca3af',
              cursor: 'pointer'
            }}
          />
        </div>
      </Card.Body>

      {/* Optional: Tooltip on hover for more details */}
      <style>{`
        .deal-card-compact:hover .position-absolute {
          opacity: 1 !important;
        }
      `}</style>
    </Card>
  );
};

export default DealCardCompact;